import type { TFunction } from 'i18next';

import { isValidEmail, isOnlyNumbers, isValidNumberOfCharacters } from './validations';
import type { FormContent, Collaborator, ElementProps } from '../../../@types/forms/types';

export interface ValidationError {
  message: string;
  page: number;
  label: string;
}

export type FieldErrors = Record<string, ValidationError>;

/**
 * Validates a single form element based on its validation rules
 * @param element - The form element to validate
 * @param content - Current form content
 * @param t - Translation function
 * @returns ValidationError object if validation fails, undefined otherwise
 */
export const validateElement = (
  element: ElementProps,
  content: FormContent[],
  t: TFunction
): ValidationError | undefined => {
  const fieldContent = content.find((c) => c.id === element.id);
  const isEmpty =
    !fieldContent ||
    !fieldContent.value ||
    fieldContent.value.length === 0 ||
    fieldContent.value.every((v) => !v || v.trim() === '');

  // 1. Required validation
  if (element.required && isEmpty) {
    return {
      message: t('forms.validationErrorMessage.fieldRequired'),
      page: element.page || 1,
      label: element.label || '',
    };
  }

  // Skip other validations if field is empty but not required
  if (isEmpty) return undefined;

  // Get the field value (use first element of value array)
  const fieldValue = fieldContent.value[0] || '';

  // 2. Email validation (validations.value === 2)
  if (element.validations?.value === 2) {
    if (!isValidEmail(fieldValue)) {
      return {
        message: t('forms.validationErrorMessage.invalidEmailFormat'),
        page: element.page || 1,
        label: element.label || '',
      };
    }
  }

  // 3. OnlyNumbers validation (validations.value === 1)
  if (element.validations?.value === 1) {
    if (!isOnlyNumbers(fieldValue)) {
      return {
        message: t('forms.validationErrorMessage.notNumber'),
        page: element.page || 1,
        label: element.label || '',
      };
    }
  }

  // 4. NumberOfCharacters validation (validations.value === 3)
  if (element.validations?.value === 3) {
    if (element.numberOfCharacters && element.numberOfCharacters.length >= 2) {
      const [min, max] = element.numberOfCharacters;
      if (!isValidNumberOfCharacters(fieldValue, min, max)) {
        return {
          message: t('forms.validationErrorMessage.InvalidNumberOfCharactersMinMax', {
            min,
            max,
          }),
          page: element.page || 1,
          label: element.label || '',
        };
      }
    }
  }

  return undefined;
};

/**
 * Filters elements based on collaborator assignment
 * @param elements - All form elements
 * @param currentUserEmail - Current user's email
 * @param collaborators - List of collaborators
 * @returns Filtered elements that should be validated for current user
 */
export const getAssignedElements = (
  elements: ElementProps[],
  currentUserEmail: string | undefined,
  collaborators: Array<{ email?: string; fields?: string[] }>
): ElementProps[] => {
  if (!currentUserEmail) {
    return elements;
  }

  const currentCollaborator = collaborators.find(
    (c) => c.email?.toLowerCase() === currentUserEmail.toLowerCase()
  );

  if (!currentCollaborator) {
    // No collaborator - return all elements
    return elements;
  }

  // Filter to only assigned fields
  const assignedFields = currentCollaborator.fields || [];
  return elements.filter((el) => assignedFields.includes(el.id));
};

/**
 * Gets required fields for display in the required fields table
 * @param elements - All form elements
 * @param currentUserEmail - Current user's email
 * @param collaborators - List of collaborators
 * @returns Array of required fields with id, label, and page, sorted by page
 */
export const getRequiredFields = (
  elements: ElementProps[],
  currentUserEmail: string | undefined,
  collaborators: Array<{ email?: string; fields?: string[] }>
): Array<{ id: string; label: string; page: number }> => {
  const assignedElements = getAssignedElements(elements, currentUserEmail, collaborators);

  return assignedElements
    .filter((el) => el.required)
    .map((el) => ({
      id: el.id,
      label: el.label || '',
      page: el.page || 1,
    }))
    .sort((a, b) => a.page - b.page);
};

/**
 * Filters field errors to only show errors for fields visible to current user
 * @param fieldErrors - All field errors
 * @param requiredFields - Required fields visible to current user
 * @returns Filtered field errors
 */
export const getVisibleFieldErrors = (
  fieldErrors: FieldErrors,
  requiredFields: Array<{ id: string; label: string; page: number }>
): FieldErrors => {
  const requiredFieldIds = new Set(requiredFields.map((field) => field.id));
  const filtered: FieldErrors = {};

  Object.keys(fieldErrors).forEach((fieldId) => {
    if (requiredFieldIds.has(fieldId)) {
      filtered[fieldId] = fieldErrors[fieldId];
    }
  });

  return filtered;
};

export interface FieldInfo {
  id: string;
  label: string;
  page: number;
  required: boolean;
}

export interface CollaboratorFieldsGroup {
  collaboratorId: string;
  role: 'owner' | 'collaborator';
  email: string | undefined;
  color: string;
  fields: FieldInfo[];
}

/**
 * Gets all fields grouped by collaborator for display
 * @param elements - All form elements
 * @param collaborators - List of collaborators
 * @param currentUserEmail - Current user's email
 * @returns Array of collaborator groups with their fields, sorted by collaborator order
 */
export const getFieldsGroupedByCollaborator = (
  elements: ElementProps[],
  collaborators: Collaborator[],
  currentUserEmail: string | undefined
): CollaboratorFieldsGroup[] => {
  const isOwner = collaborators.find(
    (c) => c.role === 'owner' && c.email?.toLowerCase() === currentUserEmail?.toLowerCase()
  );

  if (!isOwner) {
    // For collaborators, return only their own fields in a single group
    const currentCollaborator = collaborators.find(
      (c) => c.email?.toLowerCase() === currentUserEmail?.toLowerCase()
    );

    if (!currentCollaborator) {
      return [];
    }

    const assignedFieldIds = new Set(currentCollaborator.fields || []);
    const assignedElements = elements.filter((el) => assignedFieldIds.has(el.id));

    return [
      {
        collaboratorId: currentCollaborator.collaboratorId,
        role: currentCollaborator.role,
        email: currentCollaborator.email,
        color: currentCollaborator.color,
        fields: assignedElements
          .map((el) => ({
            id: el.id,
            label: el.label || '',
            page: el.page || 1,
            required: el.required || false,
          }))
          .sort((a, b) => a.page - b.page),
      },
    ];
  }

  // For owner, return all collaborators with their fields
  return collaborators
    .sort((a, b) => a.order - b.order)
    .map((collaborator) => {
      const assignedFieldIds = new Set(collaborator.fields || []);
      const assignedElements = elements.filter((el) => assignedFieldIds.has(el.id));

      return {
        collaboratorId: collaborator.collaboratorId,
        role: collaborator.role,
        email: collaborator.email,
        color: collaborator.color,
        fields: assignedElements
          .map((el) => ({
            id: el.id,
            label: el.label || '',
            page: el.page || 1,
            required: el.required || false,
          }))
          .sort((a, b) => a.page - b.page),
      };
    })
    .filter((group) => group.fields.length > 0);
};
